import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Rocket, Lock, KeyRound, ArrowRight, Check, ArrowLeft } from "lucide-react";
import PromoBanner from "../components/PromoBanner";
import { usePromo } from "../hooks/usePromo";
import {
  Navbar,
  ServicesGrid,
  FAQSection,
  FinalCTA,
  Footer,
  ContactModal,
} from "./LandingPage";

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

function EngagementTimeline() {
  const steps = [
    {
      icon: Rocket,
      tag: "JOUR 1",
      title: "Souscription & mise en ligne",
      desc: "Vous signez votre contrat en ligne et votre site est livré au maximum sous 10 jours ouvrés. Création incluse, aucun frais de lancement.",
    },
    {
      icon: Lock,
      tag: "MOIS 1 → 6",
      title: "Engagement initial de 6 mois",
      desc: "Votre abonnement court sur une période initiale de 6 mois (hébergement, maintenance, nom de domaine et support inclus). Pas de résiliation anticipée durant cette période.",
    },
    {
      icon: KeyRound,
      tag: "DÈS LE 6ᵉ MOIS",
      title: "Rachat possible ou liberté totale",
      desc: "À l'issue des 6 mois, vous pouvez activer la clause de rachat et devenir propriétaire de votre site (Active 150 € · Croissance 250 €), ou continuer en mensuel résiliable à tout moment.",
    },
  ];
  return (
    <section className="py-20 lg:py-28 bg-[#1F3535] relative overflow-hidden" data-testid="engagement-timeline">
      <div className="absolute inset-0 bg-tech-grid-dark opacity-[0.07] pointer-events-none" aria-hidden="true" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-transparent" aria-hidden="true" />
      <div className="max-w-5xl mx-auto px-6 relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.p variants={fadeUp} className="font-mono-tech text-xs tracking-widest text-[#4FD8E8] mb-4 text-center">
            [ ENGAGEMENT // COMMENT ÇA MARCHE ]
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-medium text-white text-center mb-4 font-montserrat tracking-tight">
            Un engagement de <span className="font-playfair italic text-[#E8D8A9] font-normal">6 mois</span>, puis vous êtes libre.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/60 text-sm sm:text-base font-body text-center max-w-2xl mx-auto mb-14 leading-relaxed">
            Transparence totale sur la durée. Voici exactement comment se déroule votre abonnement, du premier jour au rachat éventuel de votre site.
          </motion.p>

          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`engagement-step-${i}`}
                className="tech-bracket relative bg-[#163029] border border-white/10 rounded-2xl p-6 hover:border-[#00E5FF]/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-11 h-11 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#4FD8E8]" strokeWidth={1.7} />
                  </div>
                  <span className="font-mono-tech text-[10px] tracking-widest text-[#4FD8E8]/80 bg-[#00E5FF]/5 border border-[#00E5FF]/20 rounded px-2 py-0.5">
                    {s.tag}
                  </span>
                </div>
                <span className="font-mono-tech text-[11px] text-white/35">0{i + 1}</span>
                <h3 className="text-lg font-medium text-white mb-2 mt-1 font-montserrat tracking-tight">{s.title}</h3>
                <p className="text-sm text-white/65 leading-relaxed font-body">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.p variants={fadeUp} className="text-center text-xs text-white/45 mt-8 font-body max-w-2xl mx-auto">
            Un changement de forfait en cours de route ne réinitialise pas votre période d'engagement : elle court à compter de votre souscription initiale.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

export default function ServicesTarifsPage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [contactPrefill, setContactPrefill] = useState(null);
  const { state: promo } = usePromo();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setContactPrefill(e?.detail?.message || null);
      setContactOpen(true);
    };
    document.addEventListener("open-contact", handler);
    return () => document.removeEventListener("open-contact", handler);
  }, []);

  return (
    <div className="bg-[#FBF9F5] min-h-screen text-[#333333]">
      <Helmet>
        <title>Services & Tarifs — MonBeauSite | Forfaits site internet dès 39€/mois</title>
        <meta name="description" content="Découvrez les forfaits MonBeauSite : La Vitrine Active (39€/mois) et La Formule Croissance (59€/mois). Comparatif détaillé, options, studio branding. Création incluse, engagement 6 mois, hébergement inclus." />
        <link rel="canonical" href="https://monbeausite.com/services-et-tarifs" />
        <meta name="keywords" content="tarifs site internet, forfait site vitrine, prix création site web commerçant, abonnement site internet artisan, comparatif offres site web" />
      </Helmet>

      <PromoBanner promo={promo} />
      <Navbar onContact={() => setContactOpen(true)} />

      {/* Page header */}
      <header
        className="relative overflow-hidden border-b border-[#BFBFBF]/40 bg-[#F2EEE8]/40"
        style={{ paddingTop: "calc(var(--promo-h, 0px) + 7rem)" }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-6 pb-16 text-center">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-1.5 text-xs font-body text-[#6B6B6B] hover:text-[#C46A4A] transition-colors mb-6 nav-text tracking-wide uppercase"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à l'accueil
          </Link>
          <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-5 inline-flex justify-center">
            [ SERVICES &amp; TARIFS ]
          </p>
          <h1 className="text-4xl sm:text-5xl font-medium mb-5 text-[#1F3535] font-montserrat tracking-tight leading-[1.1]">
            Des offres claires,{" "}
            <span className="font-playfair italic text-[#C46A4A] font-normal">sans surprise.</span>
          </h1>
          <p className="text-[#6B6B6B] max-w-xl mx-auto leading-relaxed font-body font-light">
            Deux forfaits clé en main pour mettre votre activité en ligne. Création incluse (jusqu'à 3 pages),
            hébergement et maintenance inclus, à partir de moins de 2 € par jour.
          </p>
        </div>
      </header>

      <ServicesGrid onContact={() => setContactOpen(true)} promo={promo} variant="full" />

      <EngagementTimeline />

      <FAQSection />

      <FinalCTA onContact={() => setContactOpen(true)} />

      <Footer />

      <ContactModal
        open={contactOpen}
        onClose={() => { setContactOpen(false); setContactPrefill(null); }}
        prefillMessage={contactPrefill}
      />
    </div>
  );
}
